import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type InvoiceStatus = "BORRADOR" | "EMITIDA" | "PARCIAL" | "PAGADA" | "VENCIDA" | "ANULADA";

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  BORRADOR: "Borrador",
  EMITIDA: "Emitida",
  PARCIAL: "Pago parcial",
  PAGADA: "Pagada",
  VENCIDA: "Vencida",
  ANULADA: "Anulada",
};

export interface InvoiceLineRecord {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  lineSubtotal: string;
  lineTax: string;
  lineTotal: string;
}

export interface InvoiceAllocationRecord {
  id: string;
  amountApplied: string;
  paymentId: string;
  payment?: { id: string; status: "REGISTRADO" | "ANULADO"; paymentNumber: number; paymentDate: string } | null;
}

export interface InvoiceRecord {
  id: string;
  clientId: string;
  invoiceNumber: number;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  subtotal: string;
  taxAmount: string;
  total: string;
  notes: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  lines: InvoiceLineRecord[];
  allocations: InvoiceAllocationRecord[];
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

/** Saldo pendiente de una factura: total menos lo aplicado por pagos activos (no anulados). */
export function invoiceBalance(invoice: InvoiceRecord): number {
  const applied = (invoice.allocations || [])
    .filter((a) => a.payment?.status === "REGISTRADO")
    .reduce((sum, a) => sum + Number(a.amountApplied), 0);
  return Math.max(0, Number(invoice.total) - applied);
}

export interface ListInvoicesFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: InvoiceStatus;
  overdue?: boolean;
  search?: string;
}

export function useInvoices(filters: ListInvoicesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["invoices", filters],
    enabled: options.enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<InvoiceRecord[]>>("/invoices", { params: filters });
      return data;
    },
  });
}

export function useInvoice(id?: string) {
  return useQuery({
    queryKey: ["invoices", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<InvoiceRecord>>(`/invoices/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface InvoiceLinePayload {
  description: string;
  quantity: number | string;
  unitPrice: number | string;
  taxRate?: number | string;
}

export interface CreateInvoicePayload {
  clientId: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  lines: InvoiceLinePayload[];
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateInvoicePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<InvoiceRecord>>("/invoices", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateInvoicePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<InvoiceRecord>>(`/invoices/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useIssueInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<InvoiceRecord>>(`/invoices/${id}/issue`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<InvoiceRecord>>(`/invoices/${id}/mark-paid`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<InvoiceRecord>>(`/invoices/${id}/void`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/invoices/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function invoiceClientName(invoice: InvoiceRecord): string | null {
  if (!invoice.client) return null;
  return invoice.client.clientType === "PERSONA_JURIDICA"
    ? invoice.client.businessName || invoice.client.tradeName || "Cliente"
    : invoice.client.fullName || "Cliente";
}
